import * as React from 'react';
import styled from 'styled-components';
import { configConsumer, ServiceView } from '@estee/elc-service';
import { PRODUCT_CTA } from '@estee/elc-service-view-names';
import { ContentSpacing } from '@estee/elc-base-theme';
import { observer } from 'mobx-react';

import { classes } from '../utils/Classes';
import { VtoShadeName } from './common/VtoShadeName';
import {
    IProductImage,
    IShadePicker,
    ISku,
    IVtoConfig,
    IVtoFeature,
    IVtoFlag
} from '~interfaces/IVto';
import { VtoShadeInfoRightContainer } from './vto-makeup/VtoShadeInfoRightContainer';
import { VtoShadeInfoLeftContainer } from './vto-makeup/VtoShadeInfoLeftContainer';
import { VtoMakeupCustomShadePicker } from './vto-makeup/VtoMakeupCustomShadePicker';

const { name: serviceName } = __serviceInfo__;

interface IVtoMakeupShadeGridProps {
    selectedSku: ISku;
    config: IVtoMakeupShadeGridConfig;
    largeImages?: IProductImage[];
    productShadePickerProps: IShadePicker;
    selectedAutoReplenishOption?: number;
    isVtoPriceHidden: boolean;
}

interface IVtoMakeupShadeGridConfig {
    vtoConfig: IVtoConfig;
    vtoFeature: IVtoFeature;
    hideProductPricePerUnit: boolean;
}

const VtoMakeupShadeGridContainer = styled.div`
    & .elc-shade-carousel {
        padding: ${ContentSpacing.space16};
    }
    & .elc-product-shade-picker-title {
        display: none;
    }
`;

const VtoMakeupCtaContainer = styled.div`
    position: absolute;
    width: 100%;
    padding: 0 ${ContentSpacing.space16};
    bottom: ${ContentSpacing.space16};
`;

const ShadeInfoContainer = styled.div`
    padding: 0 ${ContentSpacing.space16};
    overflow: hidden;
`;

const VtoShadePieChartWrapper = styled.div`
    padding: 0 ${ContentSpacing.space16};
`;

const ctaContainerClassNames = classes('vto-shade-grid-cta-container');
const vtoMakeupShadeGridClassNames = classes('vto-makeup-shade-grid');
const shadeInfoContainerClassNames = classes('vto-shade-info-container');

export const VtoMakeupShadeGrid = configConsumer([
    'vtoConfig',
    'vtoFeature',
    'hideProductPricePerUnit'
])(
    observer((props: IVtoMakeupShadeGridProps) => {
        const {
            config,
            largeImages,
            productShadePickerProps,
            selectedAutoReplenishOption,
            isVtoPriceHidden
        } = props;

        const { vtoConfig, vtoFeature, hideProductPricePerUnit } = config;

        const { selectedSku } = productShadePickerProps;

        const {
            parentProduct: { quantity, displayName, subHeader }
        } = selectedSku;

        const productCtaConfig = {
            ...vtoConfig,
            selectedSku,
            largeImages,
            selectedAutoReplenishOption,
            quantity
        };
        const { selectedShade } = selectedSku;
        const vtoMakeupFeature = vtoFeature && vtoFeature.makeup;
        const { hasProductSize } = vtoMakeupFeature as IVtoFlag;

        return (
            <VtoMakeupShadeGridContainer className={vtoMakeupShadeGridClassNames}>
                <VtoMakeupCustomShadePicker
                    vtoMakeupFeature={vtoMakeupFeature}
                    productShadePickerProps={productShadePickerProps}
                />
                <ShadeInfoContainer className={shadeInfoContainerClassNames}>
                    <VtoShadeInfoLeftContainer
                        displayName={displayName}
                        subHeader={subHeader || ''}
                    />
                    <VtoShadeInfoRightContainer
                        selectedSku={selectedSku}
                        hasProductSize={hasProductSize || false}
                        hideProductPricePerUnit={hideProductPricePerUnit || false}
                        hideProductPrice={isVtoPriceHidden}
                    />
                </ShadeInfoContainer>
                <VtoShadePieChartWrapper>
                    <VtoShadeName selectedShade={selectedShade} />
                </VtoShadePieChartWrapper>
                <VtoMakeupCtaContainer className={ctaContainerClassNames}>
                    <ServiceView
                        name={PRODUCT_CTA}
                        config={productCtaConfig}
                        serviceName={serviceName}
                    />
                </VtoMakeupCtaContainer>
            </VtoMakeupShadeGridContainer>
        );
    })
);
